import { useEffect, useState } from "react";
import { t } from "i18next";
import moment from "moment";
import {
  dateValidationPattern,
  emailValidationPattern,
} from "src/schema/validations";

import { CONSTANTS } from "@constants/index";
import { CONTACT_TYPE, FORM_FIELD } from "@constants/register";
import { FieldProps, optionsData } from "@models/components/auth.model";
import API_SERVICES from "@services/index";

function useRegister(contactType: string) {
  const [countries, setCountries] = useState<optionsData[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setIsFetching(true);
      const response = await API_SERVICES.USER.GET_REGISTER_OPTIONS();
      const arrayOfCountries = Object.entries(
        response?.data?.countries || {}
      ).map(([countryCode, countryName]) => ({
        label: countryName as string,
        value: countryCode,
      }));
      setIsFetching(false);
      setCountries([{ label: t("select"), value: "" }, ...arrayOfCountries]);
    } catch {
      /* empty */
    }
  };

  const validateDOB = (value: string) => {
    let validate = true;

    if (
      +moment(value, CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT) > +moment()
    ) {
      validate = false;
      return t("dobValidationMessage");
    }

    return validate;
  };

  const commonFields: Array<FieldProps> = [
    {
      colWidth: "col-md-5",
      label_name: t("streetNr"),
      name: "street",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-2",
      label_name: t("zipCode"),
      maxLength: 10,
      name: "zipcode",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-5",
      label_name: t("city"),
      name: "city",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: contactType === "company" ? "col-md-6" : "col-md-4",
      label_name: t("country"),
      name: "country",
      options: countries,
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: contactType === "company" ? "col-md-6" : "col-md-4",
      label_name: t("language"),
      name: "language",
      options: [
        { label: t("select"), value: "" },
        { label: t("english"), value: "EN" },
        { label: t("german"), value: "DE" },
        { label: t("french"), value: "FR" },
        { label: t("italian"), value: "IT" },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
  ];

  const singleFields: Array<FieldProps> = [
    {
      colWidth: "col-md-6",
      label_name: t("contactType"),
      name: "contactType",
      options: [
        { label: t("singlePerson"), value: CONTACT_TYPE.SINGLE },
        {
          label: t("companyWithContact"),
          value: CONTACT_TYPE.COMPANY_WITH_CONTACT,
        },
        {
          label: t("companyWithOutContact"),
          value: CONTACT_TYPE.COMPANY_WITHOUT_CONTACT,
        },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-6",
      id: "salutation",
      label_name: t("salutation"),
      name: "salutation",
      options: [
        { label: t("select"), value: "" },
        { label: t("formal"), value: "formal" },
        { label: t("informal"), value: "informal" },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-2",
      label_name: t("gender"),
      name: "gender",
      options: [
        { label: t("select"), value: "" },
        { label: t("male"), value: "male" },
        { label: t("female"), value: "female" },
        { label: t("genderNeutral"), value: "neutral" },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-5",
      id: "firstName",
      label_name: t("firstName"),
      name: "firstName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-5",
      label_name: t("lastName"),
      name: "lastName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    ...commonFields,
    {
      colWidth: "col-md-4",
      label_name: t("dateOfBirth"),
      name: "dob",
      type: FORM_FIELD.DATE_FIELD,
    },
    {
      colWidth: "col-md-8",
      id: "email",
      label_name: t("mainEmail"),
      name: "email",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-4",
      id: "telephoneNumber",
      label_name: t("PhoneNumber"),
      name: "telephoneNumber",
      type: FORM_FIELD.PHONE_FIELD,
    },
  ];

  const companyWithoutContact = [
    {
      colWidth: "col-md-6",
      label_name: t("contactType"),
      name: "contactType",
      options: [
        { label: t("singlePerson"), value: CONTACT_TYPE.SINGLE },
        {
          label: t("companyWithContact"),
          value: CONTACT_TYPE.COMPANY_WITH_CONTACT,
        },
        {
          label: t("companyWithOutContact"),
          value: CONTACT_TYPE.COMPANY_WITHOUT_CONTACT,
        },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-6",
      id: "companyName",
      label_name: t("companyName"),
      name: "companyName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    ...commonFields,
    {
      colWidth: "col-md-8",
      id: "email",
      label_name: t("mainEmail"),
      name: "email",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-4",
      id: "telephoneNumber",
      label_name: t("PhoneNumber"),
      name: "telephoneNumber",
      type: FORM_FIELD.PHONE_FIELD,
    },
  ];

  const companyWithContact = [
    {
      colWidth: "col-md-6",
      label_name: t("contactType"),
      name: "contactType",
      options: [
        { label: t("singlePerson"), value: CONTACT_TYPE.SINGLE },
        {
          label: t("companyWithContact"),
          value: CONTACT_TYPE.COMPANY_WITH_CONTACT,
        },
        {
          label: t("companyWithOutContact"),
          value: CONTACT_TYPE.COMPANY_WITHOUT_CONTACT,
        },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-6",
      id: "salutation",
      label_name: t("salutation"),
      name: "salutation",
      options: [
        { label: t("select"), value: "" },
        { label: t("formal"), value: "formal" },
        { label: t("informal"), value: "informal" },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },

    {
      colWidth: "col-md-12",
      id: "companyName",
      label_name: t("companyName"),
      name: "companyName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-2",
      label_name: t("gender"),
      name: "gender",
      options: [
        { label: t("select"), value: "" },
        { label: t("male"), value: "male" },
        { label: t("female"), value: "female" },
        { label: t("genderNeutral"), value: "neutral" },
      ],
      type: FORM_FIELD.SELECT_FIELD,
    },
    {
      colWidth: "col-md-5",
      id: "firstName",
      label_name: t("firstName"),
      name: "firstName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-5",
      label_name: t("lastName"),
      name: "lastName",
      type: FORM_FIELD.INPUT_FIELD,
    },
    ...commonFields,
    {
      colWidth: "col-md-4",
      label_name: t("dateOfBirth"),
      name: "dob",
      type: FORM_FIELD.DATE_FIELD,
    },
    {
      colWidth: "col-md-8",
      id: "email",
      label_name: t("mainEmail"),
      name: "email",
      type: FORM_FIELD.INPUT_FIELD,
    },
    {
      colWidth: "col-md-4",
      id: "telephoneNumber",
      label_name: t("PhoneNumber"),
      name: "telephoneNumber",
      type: FORM_FIELD.PHONE_FIELD,
    },
  ];

  const [activeFields, setActiveFields] = useState<FieldProps[]>(singleFields);

  const updateFields = (value: string) => {
    if (value === CONTACT_TYPE.SINGLE) {
      setActiveFields(singleFields);
    }
    if (value === CONTACT_TYPE.COMPANY_WITH_CONTACT) {
      setActiveFields(companyWithContact);
    }
    if (value === CONTACT_TYPE.COMPANY_WITHOUT_CONTACT) {
      setActiveFields(companyWithoutContact);
    }
  };

  useEffect(() => {
    updateFields(contactType);
  }, [countries]);

  const registerOptions = {
    city: {
      minLength: {
        message: t("formErrorMessage"),
        value: 3,
      },
      required: t("cityRequired"),
    },
    companyName: { required: t("companyRequired") },
    contactType: {
      required: t("contactTypeRequired"),
    },
    country: { required: t("countryRequired") },
    dob: {
      pattern: {
        message: t("dateOfBirthMessage"),
        value: dateValidationPattern,
      },
      required: false,
      validate: validateDOB,
    },
    email: {
      pattern: {
        message: t("emailMessage"),
        value: emailValidationPattern,
      },
      required: t("emailRequired"),
    },
    firstName: {
      required: t("firstNameRequired"),
    },
    gender: { required: t("genderRequired") },
    language: {
      required: t("languageRequired"),
    },
    lastName: {
      required: t("lastNameRequired"),
    },
    salutation: { required: t("salutationRequired") },
    street: {
      minLength: {
        message: t("formErrorMessage"),
        value: 3,
      },
      required: t("streetNameRequired"),
    },

    telephoneNumber: {
      maxLength: {
        message: t("phoneNumberMaxMessage"),
        value: 15,
      },
      minLength: {
        message: t("phoneNumberMinMessage"),
        value: 10,
      },
      required: false,
    },
    zipcode: {
      minLength: {
        message: t("zipCodeMessage"),
        value: 4,
      },
      required: t("zipCodeRequired"),
    },
  };

  return { activeFields, countries, isFetching, registerOptions, updateFields };
}

export default useRegister;
